import { RouteComponentProps, withRouter } from "react-router-dom";
import { TablePanelInfo } from "../../../../Components/Headings/TableHeading/TablePanelInfo"
import { appContext } from "../../../../AppContext";
import { rights } from "../../../../Common/RightsManager";
import { Box, Grid } from "@mui/material";
import { IbssPage } from "../../../../Components/Core/BasePage/IbssPage";
import IbssFilter from "../../../../Components/Forms/Filter/IbssFilter";
import IbssDataGrid from "../../../../Components/Data/DataGrid/IbssDataGrid";
import IbssActionButton, { IActionButton } from "../../../../Components/Buttons/ActionButton/IbssActionButton";
import IbssSvgIcon from "../../../../Components/Icons/SvgIcon/IbssSvgIcon";
import AddIcon from "../../../../Components/Icons/AddIcon";
import { GridColDef } from '@mui/x-data-grid';
import { IRegionDetails } from "./DataModels";
import IbssChip from "../../../../Components/Navigation/Chip/IbssChip";
import CreateIcon from '@mui/icons-material/Create';
import Helper from "../../../../Common/Helper";
import "../../../../styles/css/table.scss";
import "../../../../App.css";

class ListRegions extends IbssPage<IProps, IState>
{
    private get labels() { return appContext().labels; }
    private userCanCreate: boolean;
    private userCanUpdate: boolean;

    constructor(props: IProps)
    {
        super(props);
        this.state = {
            regions: [],
            filterText: '',
            isLoading: false,
        };

        this.userCanCreate = rights.ADMINPORTAL.Spaces.Create.has()
        this.userCanUpdate = rights.ADMINPORTAL.Spaces.Update.has()
    }

    public async componentDidMount(): Promise<void>
    {
        this.pageTitle = this.labels.HubMenuOrganisation;
        try
        {
            this.setState({ isLoading: true });
            const regionData = await appContext().ibssApiClientV1.v1.byNodeid.nodes.get<IRegionDetails>({ nodeId: 1 });
            const uniqueRegionsMap = new Map(regionData.Regions.map(region => [region.Node_Id, region]));
            const uniqueRegionsArray = Array.from(uniqueRegionsMap.values()); 
            const regions: IRegion[] = uniqueRegionsArray.map(region => ({
                id: region.Node_Id,
                name: region.Node_Name,
                code: region.Node_Code,
                type: region.Node_Level_Name,
                personCapacity: region.Node_Capacity,
                grossArea: region.Node_Size,
                isEnabled: region.Node_IsEnabled
            }));

            this.setState({ regions: regions });
        }
        finally
        {
            this.setState({ isLoading: false });
        }
    }

    private filterRegions(searchTerm: string): IRegion[]
    {
        if (!searchTerm)
        {
            return this.state.regions;
        }

        const lowerSearchTerm = searchTerm.toLowerCase();
        const filteredRegions = this.state.regions.filter(region =>
            region.name.toLowerCase().includes(lowerSearchTerm) ||
            region.code.toLowerCase().includes(lowerSearchTerm) ||
            region.type.toLowerCase().includes(lowerSearchTerm)
        );

        return filteredRegions;
    }

    private renderStatus(region: IRegion): JSX.Element
    {
        let backgroundColor: string;
        switch (region.isEnabled)
        {
            case 1:
                backgroundColor = '--ui-success-pastel';
                break;
            case 0:
                backgroundColor = '--ui-error-pastel';
                break;
            default:
                backgroundColor = '--ui-mid-tone';
                break;
        }

        return <IbssChip label={region.isEnabled ? this.labels.HubLabelActive : this.labels.HubLabelDisabled}
            sx={{
                backgroundColor: `var(${backgroundColor})`,
                color: "var(ui-text)",
            }} />;
    }

    private handleUpdate(id: number): React.ReactNode 
    {
      return this.userCanUpdate ? (
        <IbssSvgIcon onClick={() => this.props.history.push(`/portfolio-setup-spaces/edit/${id}/Region/0`)}>
          <CreateIcon />
        </IbssSvgIcon>
      ) : (
        <p>-</p>
      );
    }

    private handleCreateRight(): void 
    {
        this.props.history.push(`/portfolio-setup-spaces/edit/0/Region/0`)
    }

    public render(): JSX.Element
    {

        const filteredRegions = this.filterRegions(this.state.filterText);
        const columns: GridColDef<IRegion>[] =
            [
                {
                    field: Helper.nameOf<IRegion>('name'),
                    headerName: this.labels.HubLabelName,
                    width: 100,
                    flex: 1,
                    sortable: false
                },
                {
                    field: Helper.nameOf<IRegion>('code'),
                    headerName: this.labels.HubLabelCode,
                    width: 100,
                    flex: 1,
                    sortable: false,
                },
                {
                    field: Helper.nameOf<IRegion>('type'),
                    headerName: this.labels.HubLabelType,
                    width: 200,
                    flex: 1,
                    sortable: false,
                },
                {
                    field: Helper.nameOf<IRegion>('personCapacity'),
                    headerName: this.labels.HubLabelCapacity,
                    width: 200,
                    flex: 1,
                    sortable: false,
                },
                {
                    field: Helper.nameOf<IRegion>('grossArea'),
                    headerName: this.labels.HubLabelSize,
                    width: 200,
                    flex: 1,
                },
                {
                    field: Helper.nameOf<IRegion>('isEnabled'),
                    headerName: this.labels.HubLabelStatus,
                    width: 200,
                    flex: 1,
                    sortable: false,
                    renderCell: params => this.renderStatus(params.row),
                },
                {
                    headerName: this.labels.HubLabelAction,
                    minWidth: 120,
                    field: "",
                    flex: 1,
                    filterable: false,
                    sortable: false,
                    renderCell: (params) => <>{this.handleUpdate(params.row.id)}</>
                }
            ];

        const actionButtons: IActionButton[] =
            [
                {
                    label: this.labels.HubButtonAdd,
                    className: "addIcon",
                    icon: (
                        <IbssSvgIcon>
                            <AddIcon />
                        </IbssSvgIcon>
                    ),
                    color: "primary",
                    disabled: !this.userCanCreate,
                    onClick: () => this.handleCreateRight(),
                }
            ];

        return (
            <div className="rightPanel-main-content">
                <div className="table-panel">
                    <TablePanelInfo
                        tableHeading={this.labels.HubLabelRegion}
                        iconSrc="Region"
                    />
                    <Grid item xs={12}>
                        <IbssFilter
                            searchTerm={this.state.filterText}
                            onSearchTermChange={(event) => this.setState({ filterText: event.target.value })}
                        />
                        <Box component="div" sx={{ display: 'flex', justifyContent: 'right', alignItems: 'center', my: 1, mr: 0 }}>
                            <IbssActionButton buttons={actionButtons} />
                        </Box>
                        {
                            <Box>
                                <IbssDataGrid
                                    loading={this.state.isLoading}  
                                    disableRowSelectionOnClick
                                    checkboxSelection={false}
                                    columns={columns}
                                    rows={filteredRegions}
                                />
                            </Box>
                        }
                    </Grid>
                </div >
            </div >
        );
    }
}



export default withRouter(ListRegions);

export interface IProps extends RouteComponentProps 
{
}
export interface IState
{
    regions: IRegion[];
    filterText: string
    isLoading: boolean
}

export interface IRegion
{
    id: number;
    name: string;
    code: string;
    type: string;
    grossArea: number;
    isEnabled: number;
    personCapacity: number;
}
